import React, { useState } from "react";
import { Input } from "@fluentui/react-components";
import { TextField } from "@fluentui/react/lib/TextField";
import { Checkbox, Stack, PrimaryButton, ThemeProvider, Dropdown, IDropdownOption, TooltipHost, DirectionalHint, Toggle, Callout, DefaultButton, IconButton } from "@fluentui/react";
import customTheme from "./../../theme/customTheme";

import { stackStyles, tooltipStyles, detailsListTheme, ccaCheckBoxInvReqStyles, invFieldConfSaveButStyles, textFieldInvoiceFieldChooser, toggleStylesAutBaseExtraction, dropdownFileSrcStylesInvoices, textFieldInvConfInstrStyles, dropdownStyles, invFieldConfMainSaveButStyles, ccaToggleInvReqStyles, calloutStylesExportDocCallout, calloutContentFeedbackDirectStyles, calloutContentRecoProcessStyles, textFieldStylesDocExtractImportScheme } from "../../styles_glob";
import { TbArrowAutofitDown, TbArrowAutofitUp } from "react-icons/tb";
import styles from "./FieldSelectionForInvoices.module.css";
import { DocFieldSelectionV2 } from "../../components/DocFieldSelection/DocFieldSelectionV2";
import ScopedInvRulesCallout from "./ScopedInvRulesCallout";
import { gselectedgroup, gloggedinuser, gextractionfieldlist, gextractionfieldlistinstruction, gfile, empty_contenttype_config, empty_extraction_field_list, gextrprop, gcontenttypeconfig } from "../../interfaces";
import { toast } from "react-toastify";

import { qSaveExtractionFieldList } from "../../api/qpi";
import { assistantCodes, invPrefCurrencyOptions, finFieldList, invFieldListDividerFlag, predefinedProcessOptions, convoLangOptionsCCA, filterPriorityOptions, orderItemIndexMap } from "../../lsts";
import  InfoStrings  from "../InfoStrings/InfoStrings";
import { FieldSelectionForInvoicesInfoStrings, type FieldSelectionTooltipInfo } from "./FieldSelectionForInvoicesInfoStrings";
import { cleanTrProps, serializeLangKeys, parseLangKeys } from "../../util_glob";
import { dropdownDPAStyles, dropdownContentTypeStyles, toggleStylesAutBaseExtractionSettings } from  "../../styles_glob";
import { TbPackageExport, TbPackageImport  } from "react-icons/tb";
import { TbSettingsDown, TbSettingsUp } from "react-icons/tb";

interface FieldSelectionForInvoicesProps {
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser;
    fileList: gfile[];
    onEditSpaceSave: () => void;
}

const FieldSelectionForInvoices: React.FC<FieldSelectionForInvoicesProps> = ({ selectedGroup, loggedInUser, fileList, onEditSpaceSave }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // approval config
    const [useApproval, setUseApproval] = useState<number>(selectedGroup.useapproval  ?? 0);
    const [splitInv, setSplitInv] = useState<number>(selectedGroup.splitinv  ?? 0);

    // root config for extraction (contains fields array, excluded, deviation, etc.)
    const [extrFieldList, setExtrFieldList] = useState<gextractionfieldlist>(selectedGroup.extractionfieldlist || empty_extraction_field_list);

    // content type config (preconfig options) - postaianalysisoptions
    const [customFieldsG, setCustomFieldsG] = useState<gextrprop[]>(selectedGroup.extractionfieldlist?.custom_props?.filter((prop: gextrprop) => prop?.global_prop == true) || []);
    const [customFieldsI, setCustomFieldsI] = useState<gextrprop[]>(selectedGroup.extractionfieldlist?.custom_props?.filter((prop: gextrprop) => prop?.global_prop != true) || []);

    // fields array - fields with specific instructions
    const [selectedFields, setSelectedFields] = useState<string[]>(
        selectedGroup.extractionfieldlist?.excl?.length > 0
            ? finFieldList.filter(field => !selectedGroup.extractionfieldlist.excl.includes(field.id)).map(field => field.id)
            : finFieldList.filter(field => field.default).map(field => field.id)
    );

    // update checkboxed fields
    const toggleFieldSelection = (fieldId: string) => {
        setSelectedFields(prev => (prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId]))
    };

    // update field list props
    const updateInstruction = (fieldId: string, changedFieldKey: keyof gextractionfieldlistinstruction, newInstr: string, level: string) => {
        setExtrFieldList(prev => {
            if (prev?.instr?.some(instr => instr.fieldid === fieldId)) {
                return { ...prev, instr: prev?.instr?.map(instr =>
                    instr.fieldid === fieldId ? { ...instr, [changedFieldKey]: newInstr, level: level } : instr
                ) };
            } else {
                console.log(`Creating new instruction for fieldId: ${fieldId}`);
                let emptyInstr = { fieldid: fieldId, instr: "", level: level, validation_source: "", is_required: "no", is_key: "No" } as gextractionfieldlistinstruction;
                return { ...prev, instr: [...(prev?.instr || []), { ...emptyInstr, [changedFieldKey]: newInstr, level: level }] };
            }
        });
    };

    // update root config props
    const updateRootInstruction = (changedKey: keyof gextractionfieldlist, newInstr: number | string) => {
        setExtrFieldList(prevInstructions => {
            return { ...prevInstructions, [changedKey]: newInstr };
        });
    };

    const savePreferences = () => {
        setIsLoading(true);

        let reverseFileds = finFieldList.filter(field => !selectedFields.includes(field.id)).map(field => field.id);
        let cleanedInstructions = extrFieldList?.instr?.map(field => ({
            ...field,
            level: finFieldList.find(f => f.id === field.fieldid)?.level === "main" ? "main" : "item"
        }));

        cleanedInstructions     = cleanedInstructions?.filter(instruction => instruction?.instr != "" || instruction?.validation_source != "" || instruction?.is_required != "") || [];

        let custom_props_global = customFieldsG || [];
        custom_props_global     = custom_props_global.map(prop => ({ ...prop, name: (prop.name?.replace(/\s+/g, "_") || "").toLowerCase(), level: "main" }));

        let custom_props_item   = customFieldsI || [];
        custom_props_item       = custom_props_item.map(prop => ({ ...prop, name: ( prop.name?.replace(/\s+/g, "_") || "").toLowerCase(), level: "item" }));

        let custom_props        = custom_props_global.concat(custom_props_item);

        let fieldConf: gextractionfieldlist = {
            excl:                       reverseFileds || [],
            instr:                      cleanedInstructions || [],
            allowed_deviation:          extrFieldList?.allowed_deviation || 0,
            custom_props:               custom_props,
            use_v4_extraction:          extrFieldList?.use_v4_extraction || 0,
            use_latest_model:           extrFieldList?.use_latest_model || 0,
            enable_custom_props:        extrFieldList?.enable_custom_props || 0,
            enable_expanded_extraction: extrFieldList?.enable_expanded_extraction || 0,
            extended_cli_code:          extrFieldList?.extended_cli_code || 0,
            preferred_currency:         extrFieldList?.preferred_currency || "NONE",
            full_pre_precessing:        extrFieldList?.full_pre_precessing || 0,
            hardcode_language:          extrFieldList?.hardcode_language || 'auto',
            adv_post_check:             extrFieldList?.adv_post_check || 0,
            align_rotation:             extrFieldList?.align_rotation || 0,
            adjust_small_diffs:         extrFieldList?.adjust_small_diffs || 0,
            inv_do_callback:            extrFieldList?.inv_do_callback || 0,
            inv_callback_url:           extrFieldList?.inv_callback_url || "",
            inv_pre_agent:              extrFieldList?.inv_pre_agent || 0,
            po_editing_enabled:         extrFieldList?.po_editing_enabled || 0,
            po_delivery_tracking_on:    extrFieldList?.po_delivery_tracking_on || 0,
            allow_linked_data_editing:  extrFieldList?.allow_linked_data_editing || 0,
            do_inv_item_combining:      extrFieldList?.do_inv_item_combining || 0,
            props_to_show:              extrFieldList?.props_to_show || ""
        };

        import.meta.env.DEV && console.log('‹fieldConf›', fieldConf)

        qSaveExtractionFieldList(loggedInUser, selectedGroup, fieldConf, useApproval, splitInv).then(() => {
            setIsLoading(false);
            onEditSpaceSave();
        });
    };

    const onRenderTitle = (options?: IDropdownOption[]) => {
        const selectedItem = options && options[0];
        return (
            <span className={selectedItem?.key === "" ? "placeholder" : ""} style={{color: selectedItem?.key === "" ? "gray" : "#333", fontStyle: selectedItem?.key === "" ? "italic" : "normal"}}>
                {selectedItem?.text || "Source"}
            </span>
        );
    };

    const [calloutState, setCalloutState] = useState<{[key: string]: boolean}>({});

    const tooltipText = (info: FieldSelectionTooltipInfo) => (
        <div style={{ minWidth: 260, maxWidth: 420, padding: "2px 0" }}>
            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, marginBottom: 10, color: "#1f2937" }}>
                {info.title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {info.sections.map((section, index) => (
                    <div key={`${info.title}_${index}`} style={{ borderLeft: `5px solid ${section.color} `, paddingLeft: 12, paddingTop: 2, paddingBottom: 2, borderRadius: `5px` }}>
                        <div style={{ fontSize: 13, lineHeight: 1.45, whiteSpace: "pre-line", color: "#374151" }}>
                            {section.text}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // scoped rules callout
    const [scopedRulesOpen, setScopedRulesOpen] = useState<boolean>(false);
    const scopedRulesBtnRef                     = React.useRef<HTMLDivElement>(null);

    // import/export field config state
    const [importJsonMain, setImportJsonMain]           = useState('');
    const [importJsonItem, setImportJsonItem]           = useState('');
    const [importCalloutMain, setImportCalloutMain]     = useState(false);
    const [importCalloutItem, setImportCalloutItem]     = useState(false);
    const importBtnMainRef = React.useRef<HTMLDivElement>(null);
    const importBtnItemRef = React.useRef<HTMLDivElement>(null);

    // po props-to-show callout (edit the comma-separated props_to_show via per-key inputs)
    const [poPropsCalloutOpen, setPoPropsCalloutOpen] = useState<boolean>(false);
    const poPropsBtnRef                               = React.useRef<HTMLDivElement>(null);

    // ordered keys of orderItemIndexMap by their numeric position (0 first)
    const poPropKeys = Object.entries(orderItemIndexMap)
        .sort((a, b) => a[1] - b[1])
        .map(([key]) => key);

    // humanize a key ⇒ "purchase_order" ￫ "Purchase Order"
    const humanizePoKey = (key: string) => key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

    // props_to_show (comma string) ￫ per-position values array (length = poPropKeys.length)
    const parsePoProps = (val: string | undefined) => {
        const parts = (val || "").split(",").map(p => p.trim());
        return poPropKeys.map((_, i) => parts[i] || "");
    };

    // per-position values array ￫ props_to_show (comma string, trailing empties trimmed)
    const serializePoProps = (vals: string[]) => {
        const clean = vals.map(v => (v || "").trim());
        while (clean.length && clean[clean.length - 1] === "") clean.pop();
        return clean.join(",");
    };

    const updatePoProp = (index: number, value: string) => {
        const vals = parsePoProps(extrFieldList?.props_to_show || '');
        vals[index] = value;
        updateRootInstruction("props_to_show", serializePoProps(vals));
    };

    const getFieldsExport = (level: 'main' | 'item') => {
        const levelFields = finFieldList.filter(f => f.level === level).map(f => f.id);
        return {
            selected: selectedFields.filter(id => levelFields.includes(id)),
            instr:    extrFieldList?.instr?.filter(i => levelFields.includes(i.fieldid)) || []
        };
    };

    const handleExportFields = (level: 'main' | 'item') => {
        const data = getFieldsExport(level);
        navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
            toast.info(`${level === 'main' ? 'Main' : 'Item'} fields copied to clipboard`, { position: 'top-center', autoClose: 2000 });
        }).catch(() => toast.error('Failed to copy to clipboard', { position: 'top-center', autoClose: 2000 }));
    };

    const handleImportFields = (level: 'main' | 'item', json: string) => {
        try {
            const parsed = JSON.parse(json);
            const levelFields = finFieldList.filter(f => f.level === level).map(f => f.id);
            // merge selected fields: keep other-level selections, replace this level
            if (Array.isArray(parsed.selected)) {
                setSelectedFields(prev => [
                    ...prev.filter(id => !levelFields.includes(id)),
                    ...parsed.selected.filter((id: string) => levelFields.includes(id))
                ]);
            }
            // merge instructions: keep other-level instrs, replace this level
            if (Array.isArray(parsed.instr)) {
                setExtrFieldList(prev => ({
                    ...prev,
                    instr: [
                        ...prev?.instr?.filter(i => !levelFields.includes(i.fieldid)) || [],
                        ...parsed?.instr || []
                    ]
                }));
            }
            toast.info(`${level === 'main' ? 'Main' : 'Item'} fields imported.`, { position: 'top-center', autoClose: 2000 });
            level === 'main' ? setImportCalloutMain(false) : setImportCalloutItem(false);
        } catch {
            toast.error('Invalid JSON', { position: 'top-center', autoClose: 2000 });
        }
    };

    const is_predef_process = (extrFieldList: gextractionfieldlist, fieldId: string, predef_proc: string): boolean => {
        return (extrFieldList?.instr?.find(instruction => instruction.fieldid === fieldId)?.predefined_process ?? '') == predef_proc;
    }

    const renderFields = (level: "main" | "item") =>
        finFieldList
            .filter(field => field.level === level)
            .map((field, index) => {
                const instruction = extrFieldList?.instr?.find(instr => instr.fieldid === field.id) || {} as gextractionfieldlistinstruction;
                const sourceKey = instruction.validation_source || "";
                const chosenProcess = instruction.predefined_process || "none";
                const calloutId = `callout_${field.id}`;
                return (
                    <div key={`itf_${level}_${index}`} className={styles.invSetItemContainer}>
                        {invFieldListDividerFlag.includes(field.id) && <div className={styles.invSectDivider} />}
                        <Stack horizontal className={styles.invSetItemStack}>

                            {/* item label */}
                            <Stack.Item className={styles.invSetItemLabel}>
                                <div className={styles.invLabelDetail}>
                                    <Checkbox
                                        className={styles.invSetItemCheckbox}
                                        styles={ccaCheckBoxInvReqStyles}
                                        key={field.id}
                                        label={field.name}
                                        disabled={field.systemDisabled}
                                        checked={selectedFields.includes(field.id)}
                                        onChange={() => toggleFieldSelection(field.id)}
                                    />
                                    <div>{field.description && <div className={styles.invSetItemDetails}>{field.description}</div>}</div>
                                </div>
                            </Stack.Item>

                            {/* checkboxes - req and check */}
                            <Stack.Item className={styles.invSetItemStackField}>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'column', verticalAlign: "middle", marginTop: "8px" }}>

                                    {/* 🧈  required - check 🎚️ */}
                                    <TooltipHost content={<InfoStrings src_id={"inv_conf"} str_id="required" />} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                        <Checkbox
                                            className={styles.invRequiredCheckTwo}
                                            label="Req"
                                            styles={ccaCheckBoxInvReqStyles}
                                            disabled={false}
                                            checked={(extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.is_required || "no") === "yes"}
                                            onChange={(e, newValue) => updateInstruction(field.id, "is_required", newValue ? "yes" : "no", level)}
                                        />
                                    </TooltipHost>
                                </div>
                            </Stack.Item>

                            {/* source dropdown */}
                            <Stack.Item>
                                <TooltipHost content={<InfoStrings src_id={"inv_conf"} str_id="validation_source" />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles} className={styles.invRequiredTool}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'row', verticalAlign: "middle" }}>
                                        <Dropdown
                                            selectedKey={sourceKey}
                                            placeholder="Attached Data"
                                            onRenderTitle={onRenderTitle}
                                            onRenderOption={option => <div style={{ whiteSpace: "nowrap", padding: "4px 8px" }}>{option?.text ?? ""}</div>}
                                            options={[
                                                { key: "", text: "" },
                                                ...(fileList?.map(file => ({ key: file.fileid.toString(), text: file.name.replace(/.pdf$/, "") })) || [])
                                            ]}
                                            onChange={(e, option) => updateInstruction(field.id, "validation_source", option?.key?.toString() || "", level)}
                                            className={styles.invSetItemDropdown}
                                            styles={dropdownFileSrcStylesInvoices}
                                            disabled={fileList?.length === 0}
                                        />
                                        {/* Visualize chosen process if selected */}
                                        {sourceKey !== "" && chosenProcess !== "none" && (
                                            <span style={{ background: '#e6f7ff', color: '#005a9e', borderRadius: 4, padding: '6px 8px', fontSize: 10, marginRight: 4, marginTop: 4 }}>
                                                {predefinedProcessOptions.find(opt => opt.key === chosenProcess)?.text || chosenProcess}
                                            </span>
                                        )}
                                        {sourceKey !== '' && (
                                            <>
                                                <IconButton
                                                    id={`${calloutId}_btn`}
                                                    className={styles.invEditSourceButton}
                                                    iconProps={{ iconName: "Edit" }}
                                                    title="Edit Data Details"
                                                    ariaLabel="Edit Data Details"
                                                    onClick={() => setCalloutState(prev => ({ ...prev, [calloutId]: true }))}
                                                />
                                                {/* External Data Operations Callout */}
                                                {calloutState[calloutId] && (
                                                    <Callout
                                                        target={`#${calloutId}_btn`}
                                                        styles={calloutContentRecoProcessStyles}
                                                        className={styles.recoProcessCallout}
                                                        onDismiss={() => setCalloutState(prev => ({ ...prev, [calloutId]: false }))}
                                                        setInitialFocus
                                                    >
                                                        <div style={{ padding: 16, minWidth: 500, maxWidth: "9500vw" }}>
                                                            {
                                                                (is_predef_process(extrFieldList, field.id, "prefilter") || is_predef_process(extrFieldList, field.id, "validate")) ? (
                                                                    <div style={{ marginBottom: 12, color: "#444", fontSize: 14 }}>
                                                                        <Dropdown
                                                                            label="Filter Condition"
                                                                            selectedKey={extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.filter_condition || "eq"}
                                                                            options={[
                                                                                { key: "eq",   text: "Equals" },
                                                                                { key: "gt",   text: "Greater Than" },
                                                                                { key: "gteq", text: "Greater Than or Equal" },
                                                                                { key: "lt",   text: "Less Than" },
                                                                                { key: "lteq", text: "Less Than or Equal" },
                                                                            ]}
                                                                            styles={dropdownStyles}
                                                                            onChange={(e, option) => updateInstruction(field.id, "filter_condition", option?.key.toString() || "eq", level)}
                                                                        />
                                                                        <TextField
                                                                            label="Filter Column"
                                                                            value={extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.filter_column ?? ""}
                                                                            styles={textFieldInvConfInstrStyles}
                                                                            onChange={(e, option) => updateInstruction(field.id, "filter_column", option?.toString() || "", level)}
                                                                        />
                                                                    </div>
                                                                ) : is_predef_process(extrFieldList, field.id, "ground") ? (
                                                                    <div style={{ marginBottom: 12, color: "#444", fontSize: 14 }}>
                                                                        <TextField
                                                                            label="Grounding Column"
                                                                            value={extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.filter_column ?? ""}
                                                                            styles={textFieldInvConfInstrStyles}
                                                                            onChange={(e, option) => updateInstruction(field.id, "filter_column", option?.toString() || "", level)}
                                                                        />
                                                                        <Toggle
                                                                            className={styles.inProcessCalloutCheck}
                                                                            label="Filter Source by this column"
                                                                            inlineLabel
                                                                            styles={ccaToggleInvReqStyles}
                                                                            disabled={false}
                                                                            checked={(extrFieldList.instr.find(instruction => instruction.fieldid === field.id)?.is_key || "No") === "Yes"}
                                                                            onChange={(e, newValue) => updateInstruction(field.id, "is_key", newValue ? "Yes" : "No", level)}
                                                                        />

                                                                    </div>
                                                                ) : (
                                                                    <div style={{ marginBottom: 12, color: "#444", fontSize: 14 }}>
                                                                        <TextField
                                                                            label="Data Handling Instruction"
                                                                            value={extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.source_specific_instr ?? ""}
                                                                            styles={textFieldInvConfInstrStyles}
                                                                            onChange={(e, option) => updateInstruction(field.id, "source_specific_instr", option?.toString() || "", level)}
                                                                            multiline
                                                                            maxLength={3500}
                                                                            autoAdjustHeight={true}
                                                                        />
                                                                        <TextField
                                                                            label="Filter Column"
                                                                            description="Specify which column in the Source data corresponds to the current field"
                                                                            value={extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.filter_column ?? ""}
                                                                            styles={textFieldInvConfInstrStyles}
                                                                            onChange={(e, option) => updateInstruction(field.id, "filter_column", option?.toString() || "", level)}
                                                                        />
                                                                    </div>
                                                                )
                                                            }
                                                            {/* post-processing type selection */}
                                                            <div style={{ display: "flex", justifyContent: "space-between"  }}>
                                                                <Dropdown
                                                                    label="Process Type"
                                                                    className={styles.inProcessCalloutDropdownProcess}
                                                                    selectedKey={extrFieldList.instr.find(instruction => instruction.fieldid === field.id)?.predefined_process ?? ""}
                                                                    options={predefinedProcessOptions}
                                                                    styles={dropdownStyles}
                                                                    onChange={(e, option) => updateInstruction(field.id, "predefined_process", option?.key?.toString() || "", level)}
                                                                />
                                                                {(is_predef_process(extrFieldList, field.id, "prefilter") || is_predef_process(extrFieldList, field.id, "validate")) && (
                                                                    <Dropdown
                                                                        label="Priority"
                                                                        className={styles.inProcessCalloutDropdownPriority}
                                                                        selectedKey={extrFieldList.instr.find(instruction => instruction.fieldid === field.id)?.filter_priority ?? "5"}
                                                                        options={filterPriorityOptions}
                                                                        styles={dropdownStyles}
                                                                        onChange={(e, option) => updateInstruction(field.id, "filter_priority", option?.key?.toString() || "", level)}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div style={{ display: "flex" }}>
                                                                {is_predef_process(extrFieldList, field.id, "ground") != true && (
                                                                    <TooltipHost content={<InfoStrings src_id={"inv_conf"} str_id="is_key" />} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} className={styles.invRequiredTool}>
                                                                        <Checkbox
                                                                            className={styles.inProcessCalloutCheck}
                                                                            label="Key"
                                                                            styles={ccaCheckBoxInvReqStyles}
                                                                            disabled={false}
                                                                            checked={(extrFieldList.instr.find(instruction => instruction.fieldid === field.id)?.is_key || "No") === "Yes"}
                                                                            onChange={(e, newValue) => updateInstruction(field.id, "is_key", newValue ? "Yes" : "No", level)}
                                                                        />
                                                                    </TooltipHost>
                                                                )}

                                                                {is_predef_process(extrFieldList, field.id, "ground") != true && (
                                                                    <TooltipHost content={<InfoStrings src_id={"inv_conf"} str_id="is_or_filtering" />} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} className={styles.invRequiredTool}>
                                                                        <Checkbox
                                                                            className={styles.orFilteringCalloutCheck}
                                                                            label="OR Filtering"
                                                                            styles={ccaCheckBoxInvReqStyles}
                                                                            disabled={false}
                                                                            checked={(extrFieldList.instr.find(instruction => instruction.fieldid === field.id)?.is_or_filtering || "No") === "Yes"}
                                                                            onChange={(e, newValue) => updateInstruction(field.id, "is_or_filtering", newValue ? "Yes" : "No", level)}
                                                                        />
                                                                    </TooltipHost>
                                                                )}
                                                            </div>
                                                            <DefaultButton
                                                                text="OK"
                                                                className={styles.invSetSaveSpaceButton}
                                                                styles={invFieldConfSaveButStyles}
                                                                onClick={() => {setCalloutState(prev => ({ ...prev, [calloutId]: false }))}}
                                                                style={{ marginTop: 12 }}
                                                            />
                                                        </div>
                                                    </Callout>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </TooltipHost>
                            </Stack.Item>

                            {/* instr field */}
                            <Stack.Item className={styles.invSetItemStackText}>
                                <div className={styles.invTextCont}>
                                    <TextField
                                        className={styles.invTextFieldFieldChooser}
                                        styles={textFieldInvoiceFieldChooser}
                                        description=''
                                        multiline
                                        autoAdjustHeight={true}
                                        value={extrFieldList?.instr?.find(instruction => instruction.fieldid === field.id)?.instr || ""}
                                        disabled={!selectedFields.includes(field.id)}
                                        resizable={false}
                                        maxLength={800}
                                        onChange={(e, newValue) => updateInstruction(field.id, "instr", newValue || "", level)}
                                    />
                                </div>
                            </Stack.Item>
                        </Stack>
                    </div>
                );
            });

    const writeBackPreconfigOptionsG = (values: gextrprop[]) => {
        for (let prop of values) prop.global_prop = true;
        if (values.length > 8) {
            toast.info("You can define up to 8 custom main fields. For more information, please contact us.", { position: "top-center", autoClose: 5000 });
            setCustomFieldsG(values.slice(0, 8));
        } else {
            setCustomFieldsG(values);
        }
    };
    const writeBackPreconfigOptionsI = (values: gextrprop[]) => {
        for (let prop of values) prop.global_prop = false;
        if (values.length > 2) {
            toast.info("You can define up to 2 custom item fields. For more information, please contact us.", { position: "top-center", autoClose: 5000 });
            setCustomFieldsI(values.slice(0, 2));
        } else {
            setCustomFieldsI(values);
        }
    };

    return (
        <div className={styles.invSetMainWrap}>
            <ThemeProvider theme={detailsListTheme}>
                {/* overall stack - children gap control vrtigal spacing */}
                <Stack styles={stackStyles} tokens={{ childrenGap: 10 }} className={styles.invSetMainStack} key="fsonefs">
                    <div className={styles.invSetSectionTitle}>
                        <span>Field Selection & Custom AI Instructions</span>
                    </div>
                    <div className={styles.invHeaderConfigs}>
                        <div className={styles.invConfigCard}>
                            <div className={styles.invConfigCardTitle}>
                                General Settings
                            </div>
                            <div className={styles.invConfigCardContent}>
                                {/* Allowed Deviation */}
                                <TooltipHost directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.allowed_deviation)}>
                                    <div className={styles.contDeviation}>
                                        <div className={styles.labelDeviation}>Allowed Deviation</div>
                                        <Input
                                            type="number"
                                            step={0.1}
                                            className={styles.fieldDeviation}
                                            value={extrFieldList.allowed_deviation?.toString() || "1"}
                                            onChange={(ev, data) => updateRootInstruction("allowed_deviation", Number(data.value))}
                                            placeholder="Enter a number"
                                        />
                                        <div className={styles.invConfigSubtitleInput}>Acceptable difference between recognized and recalculated values</div>
                                    </div>
                                </TooltipHost>
                                <TooltipHost content={tooltipText(FieldSelectionForInvoicesInfoStrings.preferred_currency)} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                    <div className={styles.contDeviation}>
                                        <div className={styles.labelDeviationDropdown}>Preferred Currency</div>
                                        <Dropdown
                                            selectedKey={extrFieldList.preferred_currency?.toString() || "NONE"}
                                            placeholder="Select Currency"
                                            options={invPrefCurrencyOptions}
                                            onChange={(e, option) => updateRootInstruction("preferred_currency", option?.key as any)}
                                            styles={dropdownStyles}
                                        />
                                        <div className={styles.invConfigSubtitleInput}>Default currency for invoice processing</div>
                                    </div>
                                </TooltipHost>
                                <TooltipHost content={tooltipText(FieldSelectionForInvoicesInfoStrings.hardcode_language)} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                    <div className={styles.contDeviation}>
                                        <div className={styles.labelDeviationDropdown}>Indicative Languages</div>
                                        <Dropdown
                                            className={styles.dpaLangDropdown}
                                            options={convoLangOptionsCCA}
                                            selectedKeys={parseLangKeys(extrFieldList.hardcode_language || 'auto')}
                                            multiSelect
                                            onChange={(_, option) => {
                                                const next = new Set(parseLangKeys(extrFieldList.hardcode_language || 'auto'));
                                                if (!option) return;
                                                const k = String(option.key);
                                                if (option.selected) {
                                                    next.add(k);
                                                    if (option.key != "auto") next.delete("auto")
                                                } else {
                                                    next.delete(k);
                                                }
                                                if (next.size === 0) next.add("auto");
                                                updateRootInstruction("hardcode_language", serializeLangKeys(Array.from(next)))
                                            }}
                                            styles={dropdownDPAStyles}
                                        />
                                        <div className={styles.invConfigSubtitleInput}>Languages that the assistant should expect when handling the content</div>
                                    </div>
                                </TooltipHost>
                            </div>
                        </div>

                        {/* Second Row: Processing & Data Configuration */}
                        <div className={styles.invConfigRow}>
                            {/* Processing Options */}
                            <div className={styles.invConfigCard}>
                                <div className={styles.invConfigCardTitle}>AI Options</div>
                                <div className={styles.invConfigCardContent}>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.use_v4_extraction)}>
                                        <Toggle
                                            label="V4 Base Processing"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.use_v4_extraction?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("use_v4_extraction", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Improved file-to-text conversion</div>
                                    </TooltipHost>

                                    {/*
                                        <TooltipHost hostClassName={styles.optToolTipHostCont} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles} content={<div dangerouslySetInnerHTML={{__html: `<div>Enable the newest version of our agentic file-to-data module, which structure files into a unified format while preserving layout structure, implicit connections and data relationships.</div><div>Yeilds improved extraction accuracy and better structured data for downstream processes.</div><div>Risks not capturing edge case data.</div>`}}/>}>
                                            <Toggle
                                                label="Full Source Reconstruction (latest alpha)"
                                                inlineLabel
                                                className={styles.editSpaceCheckbox}
                                                styles={toggleStylesAutBaseExtractionSettings}
                                                disabled={false}
                                                checked={(extrFieldList.full_pre_precessing?.toString() || "0") == "1" ? true : false}
                                                onChange={(_, checked) => updateRootInstruction("full_pre_precessing", checked ? 1 : 0)}
                                            />
                                            <div className={styles.invConfigSubtitle}>Latest alpha of our agentic file-to-data module. [For Testing. Stable version enabled by default] </div>
                                        </TooltipHost>
                                    */}

                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.use_latest_model)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Latest Model (testing only)"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.use_latest_model?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("use_latest_model", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Use newest AI model [testing only]</div>
                                    </TooltipHost>


                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.adv_post_check)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Intelligent Recheck"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.adv_post_check?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("adv_post_check", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Re-review results after ingestion.</div>
                                    </TooltipHost>


                                    <TooltipHost hostClassName={styles.optToolTipHostCont} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.inv_pre_agent)}>
                                        <Toggle
                                            label="Enable File￫Meaning Reconstruction"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.inv_pre_agent?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("inv_pre_agent", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Reconstruct file to data preserving content & meaning. (beta)</div>
                                    </TooltipHost>



                                </div>
                            </div>

                            {/* Data Configuration */}
                            <div className={styles.invConfigCard}>
                                <div className={styles.invConfigCardTitle}>Expanded Data</div>
                                <div className={styles.invConfigCardContent}>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} directionalHint={DirectionalHint.leftCenter} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.enable_custom_props)}>
                                        <Toggle
                                            label="Custom Properties"
                                            inlineLabel
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.enable_custom_props?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("enable_custom_props", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Define additional custom data fields</div>

                                    </TooltipHost>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} directionalHint={DirectionalHint.leftCenter} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.enable_expanded_extraction)}>
                                        <Toggle
                                            label="Expanded Data Collection"
                                            inlineLabel
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.enable_expanded_extraction?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("enable_expanded_extraction", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Extract expanded data for improved matching and verification</div>
                                    </TooltipHost>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} directionalHint={DirectionalHint.leftCenter} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.extended_cli_code)}>
                                        <Toggle
                                            label="Extended Client Code"
                                            inlineLabel
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.extended_cli_code?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("extended_cli_code", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Extended determination for invoices with Client Item codes available</div>
                                    </TooltipHost>
                                </div>
                            </div>
                        </div>

                        {/* Third Row: Workflow Settings */}
                        <div className={styles.invConfigRow}>
                            <div className={styles.invConfigCard}>
                                <div className={styles.invConfigCardTitle}>Process</div>
                                <div className={styles.invConfigCardContent}>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.enable_approvals)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Enable Approvals"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(useApproval ?? 0) == 1}
                                            onChange={(_, checked) => setUseApproval(prev => prev == 0 ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Require approval before finalizing invoices</div>
                                    </TooltipHost>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.po_editing_enabled)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Enable PO match redaction"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.po_editing_enabled ?? 0) == 1}
                                            onChange={(_, checked) => updateRootInstruction("po_editing_enabled", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Allow users to redact the AI matched PO on invoice lines</div>
                                    </TooltipHost>
                                    {(extrFieldList.po_editing_enabled ?? 0) == 1 && (
                                        <div className={styles.poPropsToShow}>
                                            <div className={styles.labelPoPropsToShow}>PO Props Map</div>
                                            <div ref={poPropsBtnRef} className={styles.poPropsDisplay} onClick={() => setPoPropsCalloutOpen(true)}>
                                                <div className={styles.poPropsChips}>
                                                    {parsePoProps(extrFieldList.props_to_show).some(v => v !== "")
                                                        ? poPropKeys.map((key, i) => {
                                                            const val = parsePoProps(extrFieldList.props_to_show)[i];
                                                            if (!val) return null;
                                                            return (
                                                                <span key={key} className={styles.poPropsChip} title={humanizePoKey(key)}>
                                                                    <span className={styles.poPropsChipLabel}>{humanizePoKey(key)}</span>
                                                                    <span className={styles.poPropsChipValue}>{val}</span>
                                                                </span>
                                                            );
                                                        })
                                                        : <span className={styles.poPropsEmpty}>No properties configured</span>
                                                    }
                                                </div>
                                                <IconButton
                                                    className={styles.poPropsEditBtn}
                                                    iconProps={{ iconName: "Edit" }}
                                                    title="Edit Props To Show"
                                                    ariaLabel="Edit Props To Show"
                                                    onClick={(e) => { e.stopPropagation(); setPoPropsCalloutOpen(true); }}
                                                />
                                            </div>
                                            <div className={styles.invConfigSubtitleInput}>Properties to show for PO match change</div>

                                            {poPropsCalloutOpen && (
                                                <Callout
                                                    target={poPropsBtnRef}
                                                    onDismiss={() => setPoPropsCalloutOpen(false)}
                                                    directionalHint={DirectionalHint.bottomLeftEdge}
                                                    gapSpace={10}
                                                    isBeakVisible={false}
                                                    styles={calloutStylesExportDocCallout}
                                                    setInitialFocus
                                                >
                                                    <div className={styles.poPropsCalloutInner}>
                                                        <div className={styles.poPropsCalloutTitle}>PO Properties Mapping</div>
                                                        <div className={styles.poPropsCalloutSubtitle}>Set the source column name for each property. Order is fixed.</div>
                                                        <div className={styles.poPropsCalloutGrid}>
                                                            {poPropKeys.map((key, i) => (
                                                                <div key={key} className={styles.poPropsCalloutRow}>
                                                                    <div className={styles.poPropsCalloutLabel}>
                                                                        <span className={styles.poPropsCalloutIndex}>{i}</span>
                                                                        {humanizePoKey(key)}
                                                                    </div>
                                                                    <TextField
                                                                        value={parsePoProps(extrFieldList.props_to_show)[i]}
                                                                        styles={textFieldInvConfInstrStyles}
                                                                        onChange={(e, v) => updatePoProp(i, v || "")}
                                                                        placeholder="Source column name"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <DefaultButton
                                                            text="Save"
                                                            className={styles.invSetSaveSpaceButton}
                                                            styles={invFieldConfSaveButStyles}
                                                            onClick={() => setPoPropsCalloutOpen(false)}
                                                            style={{ marginTop: 12 }}
                                                        />
                                                    </div>
                                                </Callout>
                                            )}
                                        </div>
                                    )}
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.po_delivery_tracking_on)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Track PO Delivery"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.po_delivery_tracking_on ?? 0) == 1}
                                            onChange={(_, checked) => updateRootInstruction("po_delivery_tracking_on", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Subtract invoiced quantities from linked PO items on approval</div>
                                    </TooltipHost>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.allow_linked_data_editing)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Allow linked data editing"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.allow_linked_data_editing ?? 0) == 1}
                                            onChange={(_, checked) => updateRootInstruction("allow_linked_data_editing", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Add, edit and delete linked PO source data from the lists sidebar</div>
                                    </TooltipHost>
                                    {(extrFieldList.po_editing_enabled ?? 0) == 1 && (
                                        <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.do_inv_item_combining)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                            <Toggle
                                                label="Invoice Item Merge"
                                                inlineLabel
                                                className={styles.editSpaceCheckbox}
                                                styles={toggleStylesAutBaseExtractionSettings}
                                                disabled={false}
                                                checked={(extrFieldList.do_inv_item_combining ?? 0) == 1}
                                                onChange={(_, checked) => updateRootInstruction("do_inv_item_combining", checked ? 1 : 0)}
                                            />
                                            <div className={styles.invConfigSubtitle}>Enable item merging into a single PO line when detected.</div>
                                        </TooltipHost>
                                    )}
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.split_files_per_invoice)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Split Files Per Invoice (Beta)"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(splitInv ?? 0) == 1}
                                            onChange={(_, checked) => setSplitInv(prev => prev == 0 ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Separate multi-invoice files into individual invoices</div>
                                    </TooltipHost>
                                    <TooltipHost hostClassName={styles.optToolTipHostCont} content={tooltipText(FieldSelectionForInvoicesInfoStrings.align_rotation)} directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                                        <Toggle
                                            label="Auto Page Orientation"
                                            inlineLabel
                                            className={styles.editSpaceCheckbox}
                                            styles={toggleStylesAutBaseExtractionSettings}
                                            disabled={false}
                                            checked={(extrFieldList.align_rotation?.toString() || "0") == "1" ? true : false}
                                            onChange={(_, checked) => updateRootInstruction("align_rotation", checked ? 1 : 0)}
                                        />
                                        <div className={styles.invConfigSubtitle}>Auto-correct document rotation and alignment</div>
                                    </TooltipHost>
                                </div>
                            </div>

                            <div className={styles.invConfigCard}>
                                <div className={styles.invConfigCardTitle}>Additional Fine-Tuning</div>
                                <div className={styles.invConfigCardContent}>

                                    {/* Scoped Rules */}
                                    <TooltipHost hostClassName={styles.optToolTipHostContScopedRules} directionalHint={DirectionalHint.leftCenter} calloutProps={tooltipStyles} content={tooltipText(FieldSelectionForInvoicesInfoStrings.scoped_rules)}>
                                        <div ref={scopedRulesBtnRef} style={{ display: "inline-block" }}>
                                            <DefaultButton
                                                text="Scoped Rules"
                                                iconProps={{ iconName: "Org" }}
                                                onClick={() => setScopedRulesOpen(true)}
                                            />
                                        </div>
                                        <div className={styles.invConfigSubtitle}>Pre&post processing rules for unconventional cases.</div>
                                    </TooltipHost>

                                    {/* Scoped Rules Callout */}
                                    {scopedRulesOpen && (
                                        <ScopedInvRulesCallout
                                            target={scopedRulesBtnRef}
                                            loggedInUser={loggedInUser}
                                            groupid={selectedGroup.selectionId}
                                            onDismiss={() => setScopedRulesOpen(false)}
                                        />
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.invSetTable}>
                        <ThemeProvider theme={customTheme}>
                            <div className={styles.invSetCustLabelTagInput}>
                                <div className={styles.invFieldTitleOne} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                                    <span>Main Fields</span>

                                    <div ref={importBtnMainRef} style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
                                        <TooltipHost content={tooltipText(FieldSelectionForInvoicesInfoStrings.export_main)} directionalHint={DirectionalHint.bottomRightEdge} calloutProps={tooltipStyles}>
                                            <div className={styles.fieldImpExpBtn} onClick={() => handleExportFields('main')}>
                                                <TbSettingsDown size={14} />
                                            </div>
                                        </TooltipHost>
                                        <TooltipHost content={tooltipText(FieldSelectionForInvoicesInfoStrings.import_main)} directionalHint={DirectionalHint.bottomRightEdge} calloutProps={tooltipStyles}>
                                            <div className={styles.fieldImpExpBtn} onClick={() => { setImportJsonMain(''); setImportCalloutMain(true); }}>
                                                <TbSettingsUp  size={14} />
                                            </div>
                                        </TooltipHost>
                                    </div>
                                </div>

                                <div className={styles.invFieldTitleThree}>Instructions</div>

                            </div>

                            {importCalloutMain && (
                                <Callout
                                    target={importBtnMainRef}
                                    onDismiss={() => setImportCalloutMain(false)}
                                    directionalHint={DirectionalHint.bottomRightEdge}
                                    gapSpace={10}
                                    isBeakVisible={false}
                                    styles={calloutStylesExportDocCallout}
                                    setInitialFocus
                                >
                                    <div style={{ paddingTop: 5, paddingLeft: 20, paddingRight: 20, paddingBottom: 10 }}>
                                        <TextField
                                            label="Paste Main Fields JSON"
                                            multiline
                                            rows={5}
                                            value={importJsonMain}
                                            onChange={(_, v) => setImportJsonMain(v || '')}
                                            styles={textFieldStylesDocExtractImportScheme}
                                        />
                                        <PrimaryButton text="Import" onClick={() => handleImportFields('main', importJsonMain)} style={{ marginTop: 10 }} />
                                    </div>
                                </Callout>
                            )}

                            {/* 🧮 main fields */}
                            <Stack key={"mainfielditems"} tokens={{ childrenGap: 5 }} className={styles.invSetMainStackMainFields}>
                                {renderFields("main")}
                            </Stack>

                            {/* custom fields */}
                            {extrFieldList.enable_custom_props == 1 &&
                                <div className={styles.customFieldCushion}>
                                    <DocFieldSelectionV2
                                        initialProperties={customFieldsG || []}
                                        onChange={updated => {writeBackPreconfigOptionsG(updated || []);}}
                                        isRoot={true}
                                        fileList={fileList}
                                        comp_title={"Custom Main Fields"}
                                        comp_subtitle={"Define custom main fields to be obtained from the invoice."}
                                        assistanttype={assistantCodes.ipa}
                                    />
                                </div>
                            }
                            <div className={styles.invFieldTitleTwo} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                                <span>Item-Level Fields</span>
                                {/* imoport/export buttons */}
                                <div ref={importBtnItemRef} style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
                                    <TooltipHost content={tooltipText(FieldSelectionForInvoicesInfoStrings.export_item)} directionalHint={DirectionalHint.bottomRightEdge} calloutProps={tooltipStyles}>
                                        <div className={styles.fieldImpExpBtn} onClick={() => handleExportFields('item')}>
                                            <TbSettingsDown size={14} />
                                        </div>
                                    </TooltipHost>
                                    <TooltipHost content={tooltipText(FieldSelectionForInvoicesInfoStrings.import_item)} directionalHint={DirectionalHint.bottomRightEdge} calloutProps={tooltipStyles}>
                                        <div className={styles.fieldImpExpBtn} onClick={() => { setImportJsonItem(''); setImportCalloutItem(true); }}>
                                            <TbSettingsUp size={14} />
                                        </div>
                                    </TooltipHost>
                                </div>
                            </div>
                            <div className={styles.invSetHorizontalLineSecond} />

                            {/* import config json callout*/}
                            {importCalloutItem && (
                                <Callout
                                    target={importBtnItemRef}
                                    onDismiss={() => setImportCalloutItem(false)}
                                    directionalHint={DirectionalHint.bottomRightEdge}
                                    gapSpace={10}
                                    isBeakVisible={false}
                                    styles={calloutStylesExportDocCallout}
                                    setInitialFocus
                                >
                                    <div style={{ paddingTop: 5, paddingLeft: 20, paddingRight: 20, paddingBottom: 10, width: 300 }}>
                                        <TextField
                                            label="Paste Item Fields JSON"
                                            multiline
                                            rows={5}
                                            value={importJsonItem}
                                            onChange={(_, v) => setImportJsonItem(v || '')}
                                            styles={textFieldStylesDocExtractImportScheme}
                                            className={styles.calloutImportScheme}
                                        />
                                        <PrimaryButton text="Import" onClick={() => handleImportFields('item', importJsonItem)} style={{ marginTop: 10 }} />
                                    </div>
                                </Callout>
                            )}


                            {/* 🧮 item fields */}
                            <Stack key={"itemfielditems"} tokens={{ childrenGap: 5 }} className={styles.invSetMainStackItemFields}>
                                {renderFields("item")}
                            </Stack>
                            {/* custom fields */}
                            {extrFieldList.enable_custom_props == 1 &&
                                <div className={styles.customFieldCushion}>
                                    <DocFieldSelectionV2
                                        initialProperties={customFieldsI || []}
                                        onChange={updated => {writeBackPreconfigOptionsI(updated || []);}}
                                        isRoot={true}
                                        fileList={fileList}
                                        comp_title={"Custom Per Item Fields"}
                                        comp_subtitle={"Define custom item fields to be obtained from the invoice."}
                                    />
                                </div>
                            }

                        </ThemeProvider>
                    </div>
                </Stack>

                <ThemeProvider theme={customTheme}>
                    <div className={styles.invSetModalBottomButs}>
                        <div/>
                        <PrimaryButton text="Save" onClick={savePreferences} disabled={isLoading} className={styles.invSetSaveSpaceButton} styles={invFieldConfMainSaveButStyles}/>
                    </div>
                </ThemeProvider>
            </ThemeProvider>
        </div>
    );
};

export default FieldSelectionForInvoices;
